import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useMatch } from 'react-router-dom';
// `Typography` a quitté cet import avec le cul-de-sac : il n'était plus utilisé
// que par le message « Aucun stock sélectionné », parti dans NoStockGate.
import { Box, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';

// Context
import { StockProvider, useStock } from './contexts/StockContext';

// Composants de navigation
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import SuperAdminShell from './components/SuperAdminShell';
import ImpersonationBanner, { BANNER_SPACER } from './components/ImpersonationBanner';
import NoStockGate from './components/NoStockGate';

// Pages
import Login from './pages/Login';
import InvitationAccept from './pages/InvitationAccept';
import { DemanderReinitialisation, PoserNouveauMotDePasse } from './pages/ReinitialiserMotDePasse';
import Admin from './pages/Admin';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Suppliers from './pages/Suppliers';
import PurchaseOrders from './pages/PurchaseOrders';
import OrderReception from './pages/OrderReception';
import Accounting from './pages/Accounting';
import Notifications from './pages/Notifications';
import ScanProduct from './pages/ScanProduct';
import Profile from './pages/Profile';

// Services
import { getUnreadNotificationsCount } from './services/notificationService';
import { performLogout } from './services/api';

const drawerWidth = 216; // 240 * 0.9

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: 0,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    transition: theme.transitions.create(['margin-left', 'width'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    marginLeft: 0,
    width: '100%',
    minWidth: 0,
    minHeight: '100vh',
    // 🔴 LA LIGNE QUI PORTAIT LES DEUX DÉFAUTS LES PLUS VISIBLES DU LOT RU1.
    // Elle valait `'#f8fafc'` EN DUR — le gris clair du mode clair — et
    // recouvrait donc toute la zone de contenu quel que soit le mode. Mesuré :
    // `body` était bien à `rgb(15, 23, 42)`, le thème s'appliquait ; mais
    // `main` peignait `rgb(248, 250, 252)` par-dessus.
    //
    // ⚠️ ET LE SECOND DÉFAUT EN DÉCOULAIT, sans qu'aucune couleur ne soit en
    // cause : les titres de page sont des `<Typography>` NUS, qui héritaient
    // correctement de `text.primary` — soit une couleur CLAIRE en mode sombre.
    // Clair sur clair : **contraste mesuré à 1,18**, pour un seuil WCAG de 3,0.
    // Le titre était invisible, et la cause n'était pas dans le titre.
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.up('sm')]: {
      // Dans un layout flex, la sidebar prend déjà son espace (216px si ouverte, 0px si fermée)
      // Le Main commence donc après la sidebar, et on ajoute 50px de marge
      marginLeft: `50px`, // Toujours 50px de marge après la sidebar (ou depuis le bord si sidebar fermée)
      marginRight: `50px`, // Marge droite fixe de 50px
      // Largeur = 100% - largeur sidebar - marge gauche (50px) - marge droite (50px)
      width: open 
        ? `calc(100% - ${drawerWidth}px - 50px - 50px)` // Sidebar ouverte: 100% - 216px - 50px - 50px = 100% - 316px
        : `calc(100% - 50px - 50px)`, // Sidebar fermée: 100% - 50px - 50px = 100% - 100px
    },
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // Initialiser les états d'authentification de manière synchrone depuis localStorage
  const initializeAuth = () => {
    const user = localStorage.getItem('user');
    const stocks = localStorage.getItem('stocks');
    const currentStock = localStorage.getItem('currentStock');

    return {
      user: user ? JSON.parse(user) : null,
      stocks: stocks ? JSON.parse(stocks) : [],
      currentStock: currentStock ? JSON.parse(currentStock) : null,
      // Porté par localStorage pour SURVIVRE À UN RECHARGEMENT : le claim
      // `impersonator_id` est la source de vérité, mais il vit dans un cookie httpOnly,
      // illisible en JS. Écrit au départ (SuperAdminConsole), purgé au retour et par
      // performLogout.
      impersonated: localStorage.getItem('impersonated') === 'true',
    };
  };

  // États d'authentification initialisés de manière synchrone
  const initialAuth = initializeAuth();
  const [user, setUser] = useState(initialAuth.user);
  const [stocks, setStocks] = useState(initialAuth.stocks);
  const [impersonated, setImpersonated] = useState(initialAuth.impersonated);
  
  // Utiliser le contexte pour le stock actif
  const { currentStock, changeStock } = useStock();

  // Route PUBLIQUE. Le hook est appelé ICI, avec les autres : les hooks doivent
  // être inconditionnels, et les sorties anticipées commencent plus bas.
  const invitationMatch = useMatch('/invitation/:token');

  // ⚠️ DEUX ROUTES PUBLIQUES DE PLUS, montées comme celle de l'invitation :
  // les hooks sont inconditionnels et les sorties anticipées commencent plus
  // bas. Un client qui a oublié son mot de passe n'est, par définition, PAS
  // connecté — ces écrans doivent donc vivre au-dessus de la garde `!user`.
  const demandeResetMatch = useMatch('/mot-de-passe-oublie');
  const resetMatch = useMatch('/reinitialiser/:token');

  // Initialiser le stock actif si disponible dans localStorage
  useEffect(() => {
    if (initialAuth.currentStock && !currentStock) {
      changeStock(initialAuth.currentStock);
    }
  }, []);

  // Vérifier l'authentification au chargement et gérer la sidebar
  useEffect(() => {
    // Gestion de la sidebar responsive avec délai pour éviter les conflits ResizeObserver
    let resizeTimeout;
    
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (window.innerWidth < 768) {
          setSidebarOpen(false);
        } else {
          setSidebarOpen(true);
        }
      }, 200); // Délai plus long pour éviter les conflits
    };

    // Initialiser avec un délai plus long pour éviter les conflits au chargement
    const initTimeout = setTimeout(() => {
      handleResize();
    }, 300); // Délai plus long pour l'initialisation

    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      clearTimeout(initTimeout);
    };
  }, []);

  // Fonction utilitaire pour recharger le nombre de notifications non lues
  const refreshUnreadNotifications = useCallback(async () => {
    if (!user || !currentStock) {
      console.log('🚫 Notifications: user ou currentStock manquant');
      setUnreadCount(0);
      return;
    }
    try {
      console.log('📧 Appel getUnreadNotificationsCount (manuel)...');
      const count = await getUnreadNotificationsCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des notifications:', error);
    }
  }, [user, currentStock]);

  // Charger une fois les notifications non lues quand l'utilisateur / le stock changent
  useEffect(() => {
    if (!user || !currentStock) {
      setUnreadCount(0);
      return;
    }
    refreshUnreadNotifications();
  }, [user, currentStock, refreshUnreadNotifications]);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Gestion de l'authentification
  const handleLogin = (userData, stocksData) => {
    console.log('Login - User:', userData);
    console.log('Login - Stocks:', stocksData);

    // Tokens en cookies httpOnly — plus de token en localStorage/state.
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('stocks', JSON.stringify(stocksData));

    // Puis mettre à jour le state React immédiatement
    setUser(userData);
    setStocks(stocksData);
    setImpersonated(false);   // une connexion normale n'est jamais une impersonation
    if (stocksData && stocksData.length > 0) {
      localStorage.setItem('currentStock', JSON.stringify(stocksData[0]));
      changeStock(stocksData[0]);
    }

    // Pas besoin de redirection - React va automatiquement re-rendre avec le nouvel état
  };

  const handleLogout = async () => {
    await performLogout();   // best-effort : unset des cookies côté serveur + purge localStorage
    setUser(null);
    setStocks([]);
    setImpersonated(false);
    changeStock(null);
  };

  const handleStockChange = (newStock) => {
    changeStock(newStock);
  };

  /**
   * Sortie du cul-de-sac : le premier stock vient d'être créé par un ADMIN.
   *
   * ⚠️ LES DEUX GESTES SONT NÉCESSAIRES, et le second est celui qu'on oublie.
   * `changeStock` seul suffirait à faire entrer l'utilisateur dans le châssis
   * métier — mais la Sidebar tire son sélecteur de la liste `stocks`
   * (Sidebar.js:183), alimentée par `localStorage` et par la réponse de login.
   * Sans la mise à jour ci-dessous, il entrerait dans l'application SANS
   * sélecteur de stock, et le stock qu'il vient de créer serait invisible.
   *
   * Le rattachement durable est fait CÔTÉ SERVEUR (`create_stock` ajoute le
   * créateur à `user_stocks`) : à la reconnexion, `_emit_session` renverra bien
   * ce stock. Ce qui suit ne fait qu'éviter un rechargement de page.
   */
  const handleFirstStockCreated = (nouveauStock) => {
    const liste = [...stocks, nouveauStock];
    setStocks(liste);
    localStorage.setItem('stocks', JSON.stringify(liste));
    changeStock(nouveauStock);
  };

  /**
   * Fin d'impersonation. `restoredUser` = le SUPER_ADMIN restauré, ou `null` quand le
   * serveur a répondu 400 (aucune impersonation en cours : l'état local mentait).
   *
   * Retour SANS rechargement : purger l'état puis remettre le rôle SUPER_ADMIN suffit à
   * faire retomber le rendu sur le bloc `role === 'SUPER_ADMIN'` ci-dessous, donc sur la
   * console. L'aller, lui, garde son reload — il traverse un changement complet
   * d'identité, avec des cookies déjà remplacés côté serveur.
   */
  const handleImpersonationEnded = (restoredUser) => {
    // ⚠️ ORDRE IMPORTANT. `changeStock(null)` fait un setItem de la CHAÎNE "null", il
    // n'efface pas la clé (cf. StockContext.changeStock). Le removeItem doit donc venir
    // APRÈS, sinon il serait aussitôt réécrit. C'est le prérequis dur noté au ROADMAP :
    // le SUPER_ADMIN restauré n'a AUCUN stock, un currentStock résiduel le ferait
    // repartir dans le châssis métier d'une entreprise qui n'est pas la sienne.
    changeStock(null);
    localStorage.removeItem('currentStock');
    localStorage.removeItem('stocks');
    localStorage.removeItem('impersonated');

    setImpersonated(false);
    setStocks([]);

    if (restoredUser) {
      localStorage.setItem('user', JSON.stringify(restoredUser));
      setUser(restoredUser);
    }
  };

  // Props communes pour les pages
  const pageProps = {
    showSnackbar,
    currentStock,
    refreshUnreadNotifications,
    user,
  };

  // ─── ROUTE PUBLIQUE (lot 4) ────────────────────────────────────────────────
  // AVANT la garde `!user` : c'est ce qui rend la page atteignable SANS session.
  // Et avant elle aussi pour un utilisateur CONNECTÉ — il peut légitimement
  // vouloir relayer le lien, ou gérer plusieurs comptes ; la page l'avertit
  // qu'un compte SÉPARÉ sera créé et que sa session n'est pas affectée.
  //
  // ⚠️ Une GARDE DE CHEMIN, pas un routeur au sommet. Déplacer <Routes> dans
  // App() aurait placé les 11 routes internes sous une route `*`, ce qui change
  // leur résolution — et `v7_relativeSplatPath` est activé dans index.js. Ces
  // routes ayant des chemins ABSOLUS, elles auraient pu cesser de correspondre
  // SILENCIEUSEMENT. Le routeur au sommet sera le bon geste le jour où il y
  // aura plusieurs routes publiques ; il méritera alors son propre lot.
  //
  // Le BrowserRouter est DÉJÀ monté au-dessus (index.js) : rien à remonter,
  // useMatch fonctionne ici sans changement d'architecture.
  if (demandeResetMatch) {
    return <DemanderReinitialisation />;
  }

  if (resetMatch) {
    return <PoserNouveauMotDePasse token={resetMatch.params.token} />;
  }

  if (invitationMatch) {
    return <InvitationAccept token={invitationMatch.params.token} user={user} />;
  }

  // Si pas connecté, afficher la page de connexion
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // ─── CONSOLE SUPER-ADMIN (D-UI-1) ──────────────────────────────────────────
  // Ce bloc est AVANT la garde `!currentStock` ci-dessous, volontairement :
  //
  //   1. Le SUPER_ADMIN a `company_id` NULL et AUCUN stock — /auth/login lui renvoie
  //      `stocks: []`, donc `handleLogin` n'appelle jamais `changeStock` et
  //      `currentStock` reste null. La garde suivante le laisserait dans un cul-de-sac
  //      plein écran, SANS aucune route montée (<Routes> est plus bas) : il ne pourrait
  //      atteindre aucune page, pas même /profile. Et le message lui conseillerait de
  //      « contacter l'administrateur ».
  //
  //   2. Effet de bord souhaité : un `currentStock` périmé traînant dans localStorage
  //      (session ADMIN antérieure, ou impersonation) ne peut plus faire entrer le SA
  //      dans le châssis métier d'une AUTRE entreprise. Le rôle prime sur le stock.
  //
  // La console a son propre châssis (SuperAdminShell) : ni Sidebar métier, ni sélecteur
  // de stock, ni compteur de notifications — toutes ces notions sont scopées par stock.
  if (user.role === 'SUPER_ADMIN') {
    // Pas de bandeau ici : pendant une impersonation le token porte l'identité de la
    // CIBLE, donc un rôle USER ou ADMIN — jamais SUPER_ADMIN. Cette branche ne peut pas
    // être atteinte en cours d'impersonation.
    return <SuperAdminShell user={user} onLogout={handleLogout} />;
  }

  // Bandeau d'impersonation : monté dans les DEUX branches ci-dessous, y compris le
  // cul-de-sac « aucun stock ». C'est là qu'atterrit une impersonation vers un
  // utilisateur sans stock — sans bandeau, l'utilisateur y serait sans aucune issue.
  const impersonationBanner = (
    <ImpersonationBanner
      user={user}
      impersonated={impersonated}
      onEnded={handleImpersonationEnded}
    />
  );

  // Si connecté mais pas de stock sélectionné : le cul-de-sac.
  //
  // Deux publics, deux issues (cf. NoStockGate) : un USER sans stock est un cas
  // LÉGITIME — son administrateur ne lui en a pas encore attribué — et il garde
  // le message d'origine. Un ADMIN, lui, EST l'administrateur qu'on lui disait
  // de contacter : il obtient de quoi créer son premier stock sur place.
  //
  // ⚠️ L'écran est un COMPOSANT à part, et pas du JSX en ligne comme avant : il
  // a besoin de `t()`, or `AppContent` n'est pas consommateur de LanguageContext
  // et ne doit pas le devenir (le `value` du provider n'est pas mémoïsé, cf.
  // NoStockGate). L'abonnement reste donc confiné à l'écran qui en a besoin.
  if (!currentStock) {
    return (
      <NoStockGate
        user={user}
        banner={impersonationBanner}
        spacer={impersonated ? BANNER_SPACER : 0}
        onStockCreated={handleFirstStockCreated}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}>
      <Header 
        open={sidebarOpen} 
        onMenuClick={handleSidebarToggle}
        unreadCount={unreadCount}
        user={user}
        refreshUnreadNotifications={refreshUnreadNotifications}
        showSnackbar={showSnackbar}
      />
      
      <Sidebar 
        open={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        onToggle={handleSidebarToggle}
        unreadCount={unreadCount}
        currentStock={currentStock}
        onStockChange={handleStockChange}
        stocks={stocks}
        user={user}
        onLogout={handleLogout}
      />
      
      {/* pb : réserve la hauteur du bandeau fixe pour ne pas masquer le bas de page. */}
      <Main open={sidebarOpen} sx={{ pb: impersonated ? BANNER_SPACER : 0 }}>
        <DrawerHeader />
        <Box sx={{ 
          mt: 1, 
          mb: 2, 
          pl: { xs: 2, sm: 0 }, 
          pr: { xs: 2, sm: 0 } 
        }}>
          <Routes>
            <Route path="/" element={<Dashboard {...pageProps} />} />
            <Route path="/products" element={<Products {...pageProps} />} />
            {/* 🔴 LA MÊME PAGE POUR LES DEUX ADRESSES, ET C'EST TOUT LE MOTIF.
                `/products/:id` rend la LISTE, qui ouvre le détail par-dessus.
                L'écran ne change donc pas — filtre, page et défilement restent —
                mais l'URL, elle, change : le lien se partage, et « précédent »
                revient à `/products`, ce qui FERME le dialogue sans quitter
                l'écran. ⚠️ Le composant rendu étant le MÊME type à la MÊME
                position, React réconcilie sans remonter : la liste n'est pas
                rechargée quand on ouvre ou ferme le détail. */}
            <Route path="/products/:id" element={<Products {...pageProps} />} />
            <Route path="/suppliers" element={<Suppliers {...pageProps} />} />
            {/* Le détail fournisseur est un DIALOGUE monté sur une route, comme
                le produit et la commande : la liste reste derrière avec son
                filtre et sa page, l'adresse se partage, et « précédent » ferme
                le dialogue au lieu de quitter l'écran. `/suppliers/:id` était la
                seule route de détail qui manquait. */}
            <Route path="/suppliers/:id" element={<Suppliers {...pageProps} />} />
            <Route path="/purchase-orders" element={<PurchaseOrders {...pageProps} />} />
            {/* 🔴 UNE ROUTE `:id` PAR ÉCRAN, ET C'EST MESURÉ, PAS ESTHÉTIQUE.
                Le détail d'une commande s'ouvre depuis TROIS écrans. Une URL
                unique — `/purchase-orders/:id` — ferait SORTIR de l'écran
                courant : ouvrir le détail depuis la Comptabilité déposerait le
                lecteur sur Commandes en refermant. Le chemin dit d'où l'on
                regarde, pas seulement ce qu'on regarde. Motif de RU3-d′ : la
                route rend la LISTE, qui ouvre le dialogue par-dessus. */}
            {/* ⚠️ `/nouveau` AVANT `/:id` : react-router prend la route la plus
                spécifique, mais l'ordre rend l'intention lisible — et « nouveau »
                n'est pas un identifiant, il ne doit jamais être chargé comme tel. */}
            <Route path="/purchase-orders/nouveau" element={<PurchaseOrders {...pageProps} />} />
            <Route path="/purchase-orders/:id/modifier" element={<PurchaseOrders {...pageProps} />} />
            <Route path="/purchase-orders/:id" element={<PurchaseOrders {...pageProps} />} />
            <Route path="/order-reception" element={<OrderReception {...pageProps} />} />
            <Route path="/order-reception/:id" element={<OrderReception {...pageProps} />} />
            <Route path="/accounting" element={<Accounting {...pageProps} />} />
            <Route path="/accounting/:id" element={<Accounting {...pageProps} />} />
            <Route path="/notifications" element={<Notifications {...pageProps} />} />
            <Route path="/scan" element={<ScanProduct {...pageProps} />} />
            <Route path="/admin" element={<Admin {...pageProps} />} />
            <Route path="/profile" element={<Profile {...pageProps} />} />
            {/*
              Catch-all : aucune URL ne doit produire une zone de contenu VIDE.
              Sans lui, une URL inconnue laissait Header et Sidebar en place au-dessus
              d'un vide — et Login.js ne redirigeant pas après connexion (l'URL reste
              celle d'avant), un ADMIN se reconnectant depuis /super-admin tombait
              exactement là-dessus.
              N'écrase aucune route ci-dessus : react-router v6 choisit le MEILLEUR
              match par score de spécificité, et un splat `*` a le score le plus bas —
              il ne gagne que si rien d'autre ne matche. Placé en dernier par convention.
            */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Main>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {impersonationBanner}
    </Box>
  );
}

/**
 * ⚠️ `ErrorBoundary` ET `LanguageProvider` NE SONT PLUS ICI — ils sont montés
 * dans `index.js`, au-dessus de `ThemeModeProvider` (RU8-a). *La direction
 * d'écriture est une propriété de la langue ; le thème en dépend, donc il doit
 * naître sous elle.* `StockProvider` reste ici : il ne concerne que le métier,
 * et rien au-dessus de lui n'a besoin du stock courant.
 */
function App() {
  return (
    <StockProvider>
      <AppContent />
    </StockProvider>
  );
}

export default App; 